<template>
  <ElFormItem
    :prop="name"
    :label="label"
    :double="doubleWidth"
    :class="`fi-select theme-${config}`"
  >
    <ElSelect
      ref="elSelect"
      v-bind="$attrs"
      v-model="value"
      :filterable="hasData"
      :filter-method="filter"
      :clearable="clearable"
      :multiple="true"
      :collapse-tags="true"
      :collapse-tags-tooltip="true"
      @visible-change="visibleChangeHandle"
      @change="selectChange"
    >
      <div v-if="config == 'popper'" class="fi-select-filter-input">
        <el-input ref="filterInput" v-model="filterInputText" @input="filter" />
      </div>
      <EditableOptions
        :options-data="optionsData || []"
        :editor="editor"
        :reload="reload"
      >
        <slot />
      </EditableOptions>

      <div v-if="config == 'popper'" class="fi-select-bottom-buttons">
        <ElButton size="small" @click.stop="selectAll">全选</ElButton>
        <ElButton size="small" @click.stop="reverseSelect">反选</ElButton>
        <ElButton size="small" @click.stop="resetSelect">重置</ElButton>
        <ElButton type="primary" size="small" @click.stop="closeDropdown">
          确定
        </ElButton>
      </div>
      <template v-if="config == 'popper'" #empty>
        <div class="fi-select-filter-input">
          <el-input
            ref="filterInput"
            v-model="filterInputText"
            @input="filter"
          />
        </div>
        <p class="fi-select-empty"> 无数据 </p>
      </template>
    </ElSelect>

    <HelpIcon :help="help" />
  </ElFormItem>
</template>

<script lang="ts" setup>
/**
 * ### 多选下拉组件
 * <warning> 该组件样式主题受 uiTheme.multipleSelectFilter 影响
 * <warning> 获取下拉数据可从url/transform/callback三种方式中三选一
 * <warning> 可通过默认插槽渲染下拉项 但此时多选的全选/反选等操作会失效
 * 传入未指定属性会透传到 ElSelect 组件中
 */
import {
  ref,
  computed,
  nextTick,
  ComponentPublicInstance,
  onMounted,
  watch
} from 'vue';
import { ElFormItem, ElSelect } from 'element-plus';
import {
  pinyinFilterData,
  PinyinFilterCallback
} from '../../../core/CommonInternal';
import { useShareData } from '../../../core/SharedData';
import { DropdownEditor } from '../../../common/DropdownEditor';
import EditableOptions from '../../../common/EditableOptions.vue';
import HelpIcon from '../../../common/HelpIcon.vue';
import { isInteger, toInteger, isArray } from 'lodash-es';
import { getUiTheme } from '../../../core/uiTheme';

const props = withDefaults(
  defineProps<{
    /**
     * ### post参数名
     */
    name: string;
    /**
     * ### 表单项名
     */
    label: string;
    /**
     * ### 默认值
     */
    defaultValue?: (number | string)[];
    /**
     * ### 获取下拉数据的url
     * <warning> 最低优先级
     */
    url?: string;
    /**
     * ### 获取下拉数据的数据源（变换）
     * <warning> 优先级最高
     */
    transform?: string;
    /**
     * ### 获取下拉数据的函数
     * <warning> 优先级高于url低于transform
     */
    callback?: PinyinFilterCallback;
    /**
     * ### 如若使用callback方式获取下拉数据 该选项会从form model中获取同名key作为callback参数
     */
    callbackParams?: string[];
    /**
     * ### 排除下拉数据中的特定ID
     */
    excludes?: (number | string)[];
    /**
     * ### 保持id类型不变, 默认会转成number
     */
    rawId?: boolean;
    /**
     * ### 下拉列表编辑器
     */
    editor?: DropdownEditor;
    /**
     * ### 双倍宽度
     */
    double?: boolean;
    /**
     * ### 帮助信息
     */
    help?: string;
    /**
     * ### 输入是否可清除
     */
    clearable?: boolean;
  }>(),

  {
    defaultValue: () => [],
    clearable: true
  }
);

const { optionsData, filter, clearFilter, reload, hasData } = pinyinFilterData(
  props.url,
  props.transform,
  props.callback,
  props.callbackParams,
  true,
  props.excludes,
  props.rawId
);

const config = getUiTheme('multipleSelectFilter');

const filterInputText = ref('');

const filterInput = ref();

const visibleChangeHandle = (isShow: boolean) => {
  clearFilter(isShow);
  filterInputText.value = '';
  if (isShow) {
    nextTick(() => {
      filterInput.value?.focus();
    });
  }
};

// 在下拉选项中有数据和无数据(empty插槽)使用的是两个筛选input
// 导致当下拉选项从无到有或从有到无会重新渲染input 从而失去焦点
// 故需检测当filterInput发生变化时获取下焦点
watch(filterInput, () => {
  nextTick(() => {
    filterInput.value?.focus();
  });
});

const model = useShareData('form', 'model');
// eslint-disable-next-line vue/no-setup-props-destructure
const name = props.name;
// 设置默认值
if (isArray(props.defaultValue) && model[name] === undefined) {
  // eslint-disable-next-line vue/no-setup-props-destructure
  model[name] = props.defaultValue;
} else if (isArray(model[name])) {
  if (!props.rawId) {
    const arr = model[name] as any[];
    // 转成整数array
    for (let i = 0; i < arr.length; i++) {
      if (!isInteger(arr[i])) {
        arr[i] = toInteger(arr[i]);
      }
    }
  }
} else {
  // model[name] 不是数组
  console.error('FiMselect value is not array: ', name, model[name]);
}

// 在form中注册
useShareData('form', 'items').$(name)(true);

// 下拉项的值为integer, 如果传进来是字符串, 转成int
const value = computed({
  get() {
    const v = model[name];
    if (!v) return v;
    return v;
  },

  set(v: (number | string)[]) {
    model[name] = v;
  }
});

////////////////////////////////////////
// 下拉框处理
let elSelect = ref<ComponentPublicInstance<{
  focus: () => void;
  blur: () => void;
  input: HTMLInputElement;
}> | null>(null);

const selectChange = () => {
  clearFilter(false);

  const input = elSelect.value!.input;
  if (input) {
    nextTick(() => {
      // 清空输入框
      input.value = '';
      input.dispatchEvent(new Event('input'));
    });
  }
};

// self主题下加一个空的placeholder
// 以使css通过placehoder-shown判断元素是否有输入值
onMounted(() => {
  if (config == 'self') {
    nextTick(() => {
      const input = elSelect.value!.input;
      if (input) {
        input.placeholder = ' ';
      }
    });
  }
});

// 关闭dropdown popup
const closeDropdown = () => {
  elSelect.value?.blur?.();

  setTimeout(() => {
    elSelect.value?.blur?.();
  }, 0);
};

// 全选所有dropdown项
const selectAll = () => {
  const result: number[] = [];
  for (let item of Object.values(optionsData.value)) {
    result.push(item.id as number);
  }
  model[name] = result;
};

// 反选dropdown
const reverseSelect = () => {
  const result = [] as number[];
  for (let item of Object.values(optionsData.value)) {
    console.info('model[name]', model[name]);
    if (!model[name].includes(item.id)) {
      result.push(item.id as number);
    }
  }
  model[name] = result;
};

const resetSelect = () => {
  model[name] = [];
};

// 双倍宽度
const doubleWidth = props.double ? '' : undefined;
</script>

<style lang="scss" scoped>
.fi-select {
  height: var(--fi-line-height);
  font-size: var(--fi-font-size);

  :deep(.el-input) {
    height: var(--fi-line-height);

    --el-input-height: calc(var(--fi-line-height) - 2px);
    --el-font-size-base: var(--fi-font-size);
    --el-input-icon-color: #8f959e;
  }

  :deep(.el-input__wrapper) {
    box-shadow: none;
    border-radius: var(--fi-border-radius);
    border: 1px solid var(--fi-border-color);
    padding-top: 0;
    padding-bottom: 0;
  }

  :deep(.el-select__tags) {
    flex-wrap: nowrap;
  }

  .search-input {
    position: sticky;
    left: 0;
    top: 0;
    border: 1px solid red;
  }

  &.theme-popper {
    :deep(.el-select__tags) {
      cursor: pointer;
      input {
        visibility: hidden;
      }
    }
  }

  &.theme-self {
    :deep(.el-select__tags:focus-within:has(.el-select-tags-wrapper)) {
      input {
        position: absolute;
        inset: 0;
        &:not(:placeholder-shown) {
          width: 100% !important;
          backdrop-filter: blur(3px);
          background: var(--el-fill-color);
        }
      }
    }
  }

  :deep(.el-input__wrapper:hover) {
    box-shadow: none !important;
    border-color: var(--fi-border-hover-color);
  }

  :deep(.el-input.is-focus) {
    box-shadow: none;
  }

  :deep(.el-input.is-focus .el-input__wrapper) {
    box-shadow: none !important;
    border-color: var(--fi-border-hover-color);
  }

  :deep(.el-input__prefix) {
    max-width: var(--fi-prefix-width);
    margin-right: var(--fi-prefix-right-space);
    padding-left: 12px;
  }

  :deep(.el-input__prefix-inner) {
    display: block;
    max-width: var(--fi-prefix-width);
    overflow: hidden;
    text-overflow: ellipsis;
    text-align: left;
  }

  :deep(.el-select-tags-wrapper) {
    white-space: nowrap;
  }

  :deep(.el-input__prefix-inner:hover) {
    overflow: visible;
  }

  .el-select + .el-icon {
    margin-left: 8px;
  }
}

.fi-select[double] {
  grid-column-start: span 2;
}

.fi-select-filter-input {
  position: sticky;
  top: 0;
  margin: 0 10px;
  background: #fff;
  z-index: 1;
  border-radius: 4px;
  cursor: text;
  transition: var(--el-transition-box-shadow);
  transform: translateZ(0);
  border: 1px solid #dcdfe6;
}
.fi-select-bottom-buttons {
  text-align: center;
  position: sticky;
  bottom: 0px;
  background: #fff;
  z-index: 1;
  padding: 5px;
  white-space: nowrap;
  .el-button--primary {
    background-color: #409eff;
  }
  .el-button--primary:hover {
    background-color: #79bbff;
  }
}

.fi-select-empty {
  padding: 10px 0;
  margin: 0;
  text-align: center;
  color: var(--el-text-color-secondary);
  font-size: var(--el-select-font-size);
}
</style>
